/**
 * What did the browser actually record?
 *
 * This answers one question honestly, because the archive depends on it: a
 * sealed video is opened once, years from now, by software nobody has written
 * yet. The only thing that software has to go on is the manifest we wrote at
 * seal time — codec, container, mime. If that manifest is wrong, we have
 * handed the future a confident wrong instruction, which is worse than none.
 *
 * It was wrong. `codec` was hardcoded to 'h264-aac' and the mime type derived
 * from it, while the container came honestly from the recorded blob. Chrome
 * records `video/webm;codecs=vp9,opus`, so production held a VP9/Opus WebM
 * filed as H.264/AAC in an MP4 — two of the three fields untrue. Playback
 * survived only because browsers read the real container and ignore the label.
 * ADR-004 plans a V1.1 job that re-archives every message by trusting exactly
 * that label.
 *
 * MediaRecorder reports what it produced in `blob.type`, e.g.
 *   video/webm;codecs=vp9,opus     video/webm;codecs=vp8,opus
 *   video/mp4                      video/webm
 * so the truth is available; it simply was not being read.
 */
/** Video codecs we can name. Order matters: vp9 before vp8 before h264. */
const VIDEO_CODECS = [
    [/\bvp9|vp09\b/i, 'vp9'],
    [/\bvp8\b/i, 'vp8'],
    [/\bav01|av1\b/i, 'av1'],
    [/\bh264|avc1|avc3\b/i, 'h264'],
    [/\bh265|hevc|hvc1\b/i, 'h265'],
];
const AUDIO_CODECS = [
    [/\bopus\b/i, 'opus'],
    [/\bvorbis\b/i, 'vorbis'],
    [/\baac|mp4a\b/i, 'aac'],
];
/**
 * Read the format out of a MediaRecorder blob type.
 *
 * When the browser does not spell out its codecs — Safari often reports a bare
 * `video/mp4` — fall back to what that container overwhelmingly means rather
 * than inventing precision we do not have. The container is always true, so a
 * consumer that trusts only the container is never misled.
 */
export function readRecordedFormat(blobType, kind = 'video') {
    const type = (blobType ?? '').trim().toLowerCase();
    const container = type.includes('mp4') ? 'mp4' : 'webm';
    // String() is not decoration: this module is compiled to plain JS and
    // vendored into the apps, where the tuple types are gone and `[1]` would
    // otherwise widen to string | RegExp at every call site.
    const audio = String(AUDIO_CODECS.find(([re]) => re.test(type))?.[1]
        ?? (container === 'mp4' ? 'aac' : 'opus'));
    if (kind === 'voice') {
        return { codec: audio, container, mimeType: container === 'mp4' ? 'audio/mp4' : 'audio/webm' };
    }
    const video = String(VIDEO_CODECS.find(([re]) => re.test(type))?.[1]
        // A bare container tells us the default the browser would have used: MP4
        // recorders emit H.264, WebM recorders emit VP8 unless they say otherwise.
        ?? (container === 'mp4' ? 'h264' : 'vp8'));
    return {
        codec: `${video}-${audio}`,
        container,
        mimeType: container === 'mp4' ? 'video/mp4' : 'video/webm',
    };
}
